import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ScrollText, AlertCircle, Plus, FileText, X, Upload, Trash2, Download, Share2, Eye, ExternalLink, FileImage, FileType, Calendar } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Modal } from '../common/Modal';
import { TrustBuilder } from './TrustBuilder';

interface WillDocument {
  id: string;
  title: string;
  type: 'will' | 'trust';
  status: string;
  content: string;
  file_path?: string;
  file_name?: string;
  file_size?: number;
  file_type?: string;
  file_url?: string;
  last_reviewed?: string;
  shared_with?: string[];
  created_at: string;
  updated_at: string;
}

interface WillFormData {
  testator: {
    name: string;
    address: string;
    maritalStatus: string;
  };
  executor: {
    name: string;
    address: string;
    relationship: string;
  };
  alternateExecutor: {
    name: string;
    address: string;
    relationship: string;
  };
  beneficiaries: Array<{
    name: string;
    relationship: string;
    share: string;
    specificGifts?: string;
  }>;
  guardians?: Array<{
    name: string;
    relationship: string;
  }>;
  specificBequests: Array<{
    item: string;
    recipient: string;
    description: string;
  }>;
  residualEstate: {
    distribution: string;
    charities?: Array<{
      name: string;
      percentage: number;
    }>;
  };
  finalWishes: {
    funeral: string;
    burial: string;
    organDonation: string;
  };
}

const initialFormData: WillFormData = {
  testator: {
    name: '',
    address: '',
    maritalStatus: '',
  },
  executor: {
    name: '',
    address: '',
    relationship: '',
  },
  alternateExecutor: {
    name: '',
    address: '',
    relationship: '',
  },
  beneficiaries: [{
    name: '',
    relationship: '',
    share: '',
  }],
  guardians: [],
  specificBequests: [],
  residualEstate: {
    distribution: 'equal',
    charities: [],
  },
  finalWishes: {
    funeral: '',
    burial: '',
    organDonation: '',
  },
};

export function WillsAndTrusts() {
  const { user } = useAuth();
  const location = useLocation();
  const [documents, setDocuments] = useState<WillDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showWillBuilder, setShowWillBuilder] = useState(false);
  const [showTrustBuilder, setShowTrustBuilder] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<WillFormData>(initialFormData);
  const [uploadType, setUploadType] = useState<'will' | 'trust'>('will');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareDocId, setShareDocId] = useState<string | null>(null);
  const [shareEmails, setShareEmails] = useState('');
  const [viewDoc, setViewDoc] = useState<WillDocument | null>(null);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [pdfViewerUrl, setPdfViewerUrl] = useState<string | null>(null);
  const [shareForm, setShareForm] = useState({
    email: '',
    accessLevel: 'view',
    note: ''
  });

  useEffect(() => {
    if (user) {
      loadDocuments();
    }
  }, [user]);

  useEffect(() => {
    const handler = () => { if (user) loadDocuments(true); };
    window.addEventListener('refresh-legal', handler);
    return () => window.removeEventListener('refresh-legal', handler);
  }, [user]);

  useEffect(() => {
    if ((location.state as any)?.openWillBuilder) {
      setShowWillBuilder(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  async function loadDocuments(background = false) {
    try {
      if (!background) setLoading(true);
      const { data, error } = await supabase
        .from('legal_documents')
        .select('*')
        .eq('user_id', user?.id)
        .in('type', ['will', 'trust']);

      if (error) throw error;
      setDocuments(data || []);
    } catch (err) {
      if (!background) setError(err instanceof Error ? err.message : 'Failed to load documents');
    } finally {
      if (!background) setLoading(false);
    }
  }

  const handleInputChange = (section: keyof WillFormData, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSave = async () => {
    try {
      const { data, error } = await supabase
        .from('legal_documents')
        .insert([
          {
            user_id: user?.id,
            title: `Last Will and Testament - ${formData.testator.name}`,
            type: 'will',
            status: 'draft',
            content: JSON.stringify(formData),
          },
        ])
        .select()
        .single();

      if (error) throw error;

      setDocuments(prev => [...prev, data]);
      setShowWillBuilder(false);
      setFormData(initialFormData);
      setCurrentStep(0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save will');
    }
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file size (50MB limit)
    if (file.size > 50 * 1024 * 1024) {
      setError('File size must be less than 50MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      setError('File type not supported. Please upload PDF, DOC, DOCX, or image files.');
      return;
    }

    try {
      setError(null);
      setUploading(true);
      const filePath = `${user?.id}/${uploadType}s/${Date.now()}_${file.name}`;
      
      // Upload file to storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('legal-documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL for the file
      const { data: urlData } = supabase.storage
        .from('legal-documents')
        .getPublicUrl(filePath);

      // Create document record in database
      const { data: docData, error: dbError } = await supabase
        .from('legal_documents')
        .insert([
          {
            user_id: user?.id,
            title: `Uploaded ${uploadType === 'will' ? 'Will' : 'Trust'} - ${file.name}`,
            type: uploadType,
            status: 'active',
            content: JSON.stringify({ file_path: filePath }),
            file_path: filePath,
            file_name: file.name,
            file_size: file.size,
            file_type: file.type,
            file_url: urlData.publicUrl,
            last_reviewed: new Date().toISOString(),
          },
        ])
        .select()
        .single();

      if (dbError) throw dbError;

      setShowUploadModal(false);
      loadDocuments(); // Reload documents to show the newly uploaded one
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  // Download file from Supabase Storage
  const handleDownload = async (doc: WillDocument) => {
    try {
      setDownloading(doc.id);
      const filePath = doc.file_path || JSON.parse(doc.content || '{}')?.file_path;
      if (!filePath) throw new Error('No file path found');
      
      const { data, error } = await supabase.storage
        .from('legal-documents')
        .download(filePath);
      
      if (error) throw error;
      
      const url = window.URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || doc.title;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download document');
    } finally {
      setDownloading(null);
    }
  };

  // Delete file from Supabase Storage and DB
  const handleDelete = async (doc: WillDocument) => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) return;
    
    try {
      setDeleting(doc.id);
      const filePath = doc.file_path || JSON.parse(doc.content || '{}')?.file_path;
      if (filePath) {
        const { error: storageError } = await supabase.storage
          .from('legal-documents')
          .remove([filePath]);
        if (storageError) throw storageError;
      }
      
      const { error: dbError } = await supabase
        .from('legal_documents')
        .delete()
        .eq('id', doc.id);
      
      if (dbError) throw dbError;
      
      loadDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete document');
    } finally {
      setDeleting(null);
    }
  };

  // View document (open file or modal)
  const handleView = (doc: WillDocument) => {
    const filePath = doc.file_path || JSON.parse(doc.content || '{}')?.file_path;
    if (filePath) {
      try {
        const { data } = supabase.storage
          .from('legal-documents')
          .getPublicUrl(filePath);
        
        if (data.publicUrl) {
          // Check if it's a PDF to show in built-in viewer
          if (doc.file_type === 'application/pdf') {
            setPdfViewerUrl(data.publicUrl);
          } else {
            window.open(data.publicUrl, '_blank');
          }
        } else {
          setError('Unable to generate view URL for document');
        }
      } catch (err) {
        setError('Failed to generate view URL for document');
      }
    } else {
      setViewDoc(doc);
    }
  };

  // Share document modal
  const handleShare = (doc: WillDocument) => {
    setShareDocId(doc.id);
    setShareForm({
      email: '',
      accessLevel: 'view',
      note: ''
    });
    setShowShareModal(true);
  };

  const handleShareSubmit = async () => {
    if (!shareDocId || !shareForm.email.trim()) return;
    
    try {
      const shareData = {
        email: shareForm.email.trim(),
        accessLevel: shareForm.accessLevel,
        note: shareForm.note.trim(),
        sharedAt: new Date().toISOString()
      };

      // Get current shared_with array and add new share
      const currentDoc = documents.find(doc => doc.id === shareDocId);
      const currentShared = currentDoc?.shared_with || [];
      const newShared = [...currentShared, JSON.stringify(shareData)];

      const { error } = await supabase
        .from('legal_documents')
        .update({ shared_with: newShared })
        .eq('id', shareDocId);
      
      if (error) throw error;
      
      setShowShareModal(false);
      setShareDocId(null);
      setShareForm({ email: '', accessLevel: 'view', note: '' });
      loadDocuments();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to share document');
    }
  };

  const handleShareFormChange = (field: string, value: string) => {
    setShareForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const steps = [
    {
      title: 'Personal Information',
      component: (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold">Your Information</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Full Legal Name
              </label>
              <input
                type="text"
                value={formData.testator.name}
                onChange={(e) => handleInputChange('testator', 'name', e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Current Address
              </label>
              <textarea
                value={formData.testator.address}
                onChange={(e) => handleInputChange('testator', 'address', e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Marital Status
              </label>
              <select
                value={formData.testator.maritalStatus}
                onChange={(e) => handleInputChange('testator', 'maritalStatus', e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select status</option>
                <option value="single">Single</option>
                <option value="married">Married</option>
                <option value="divorced">Divorced</option>
                <option value="widowed">Widowed</option>
              </select>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Executor Details',
      component: (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-4">Primary Executor</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.executor.name}
                  onChange={(e) => handleInputChange('executor', 'name', e.target.value)}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <textarea
                  value={formData.executor.address}
                  onChange={(e) => handleInputChange('executor', 'address', e.target.value)}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relationship to You
                </label>
                <input
                  type="text"
                  value={formData.executor.relationship}
                  onChange={(e) => handleInputChange('executor', 'relationship', e.target.value)}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-4">Alternate Executor</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={formData.alternateExecutor.name}
                  onChange={(e) => handleInputChange('alternateExecutor', 'name', e.target.value)}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <textarea
                  value={formData.alternateExecutor.address}
                  onChange={(e) => handleInputChange('alternateExecutor', 'address', e.target.value)}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relationship to You
                </label>
                <input
                  type="text"
                  value={formData.alternateExecutor.relationship}
                  onChange={(e) => handleInputChange('alternateExecutor', 'relationship', e.target.value)}
                  className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Beneficiaries',
      component: (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Beneficiaries</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  beneficiaries: [
                    ...prev.beneficiaries,
                    { name: '', relationship: '', share: '' }
                  ]
                }));
              }}
              icon={<Plus size={16} />}
            >
              Add Beneficiary
            </Button>
          </div>

          <div className="space-y-6">
            {formData.beneficiaries.map((beneficiary, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg relative">
                <button
                  onClick={() => {
                    const newBeneficiaries = [...formData.beneficiaries];
                    newBeneficiaries.splice(index, 1);
                    setFormData({ ...formData, beneficiaries: newBeneficiaries });
                  }}
                  className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
                  disabled={formData.beneficiaries.length <= 1}
                >
                  <X size={16} />
                </button>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={beneficiary.name}
                      onChange={(e) => {
                        const newBeneficiaries = [...formData.beneficiaries];
                        newBeneficiaries[index].name = e.target.value;
                        setFormData({ ...formData, beneficiaries: newBeneficiaries });
                      }}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Relationship
                    </label>
                    <input
                      type="text"
                      value={beneficiary.relationship}
                      onChange={(e) => {
                        const newBeneficiaries = [...formData.beneficiaries];
                        newBeneficiaries[index].relationship = e.target.value;
                        setFormData({ ...formData, beneficiaries: newBeneficiaries });
                      }}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Share of Estate
                    </label>
                    <input
                      type="text"
                      value={beneficiary.share}
                      onChange={(e) => {
                        const newBeneficiaries = [...formData.beneficiaries];
                        newBeneficiaries[index].share = e.target.value;
                        setFormData({ ...formData, beneficiaries: newBeneficiaries });
                      }}
                      placeholder="e.g., 25% or specific amount"
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: 'Specific Bequests',
      component: (
        <div className="space-y-6">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Specific Bequests</h3>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  specificBequests: [
                    ...prev.specificBequests,
                    { item: '', recipient: '', description: '' }
                  ]
                }));
              }}
              icon={<Plus size={16} />}
            >
              Add Bequest
            </Button>
          </div>

          <div className="space-y-6">
            {formData.specificBequests.map((bequest, index) => (
              <div key={index} className="p-4 bg-gray-50 rounded-lg relative">
                <button
                  onClick={() => {
                    const newBequests = [...formData.specificBequests];
                    newBequests.splice(index, 1);
                    setFormData({ ...formData, specificBequests: newBequests });
                  }}
                  className="absolute top-2 right-2 text-gray-400 hover:text-red-500"
                >
                  <X size={16} />
                </button>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Item or Asset
                    </label>
                    <input
                      type="text"
                      value={bequest.item}
                      onChange={(e) => {
                        const newBequests = [...formData.specificBequests];
                        newBequests[index].item = e.target.value;
                        setFormData({ ...formData, specificBequests: newBequests });
                      }}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Recipient
                    </label>
                    <input
                      type="text"
                      value={bequest.recipient}
                      onChange={(e) => {
                        const newBequests = [...formData.specificBequests];
                        newBequests[index].recipient = e.target.value;
                        setFormData({ ...formData, specificBequests: newBequests });
                      }}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      value={bequest.description}
                      onChange={(e) => {
                        const newBequests = [...formData.specificBequests];
                        newBequests[index].description = e.target.value;
                        setFormData({ ...formData, specificBequests: newBequests });
                      }}
                      className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      rows={3}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: 'Final Wishes',
      component: (
        <div className="space-y-6">
          <h3 className="text-lg font-semibold">Final Wishes</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Funeral Arrangements
              </label>
              <textarea
                value={formData.finalWishes.funeral}
                onChange={(e) => handleInputChange('finalWishes', 'funeral', e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Describe your preferred funeral arrangements..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Burial Instructions
              </label>
              <textarea
                value={formData.finalWishes.burial}
                onChange={(e) => handleInputChange('finalWishes', 'burial', e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                rows={3}
                placeholder="Specify your burial or cremation preferences..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Organ Donation
              </label>
              <select
                value={formData.finalWishes.organDonation}
                onChange={(e) => handleInputChange('finalWishes', 'organDonation', e.target.value)}
                className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select preference</option>
                <option value="yes">Yes, I wish to donate my organs</option>
                <option value="no">No, I do not wish to donate my organs</option>
                <option value="specific">I wish to donate specific organs only</option>
              </select>
            </div>
          </div>
        </div>
      ),
    },
  ];

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return <FileText size={32} className="text-red-500" />;
    if (fileType.includes('word') || fileType.includes('document')) return <FileType size={32} className="text-blue-500" />;
    if (fileType.includes('image')) return <FileImage size={32} className="text-green-500" />;
    return <FileText size={32} className="text-gray-500" />;
  };

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Wills & Trusts</h1>
          <p className="text-gray-600 mt-1">Manage your will and trust documents</p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 bg-red-50 text-red-700 rounded-lg">
          <AlertCircle size={20} />
          <p>{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">Last Will and Testament</h2>
            <div className="space-y-4">
              <p className="text-gray-600">
                A will is a legal document that outlines your wishes for the distribution of your assets after your death.
              </p>
              <Button 
                variant="outline" 
                className="w-full justify-center"
                onClick={() => setShowWillBuilder(true)}
              >
                <ScrollText size={20} className="mr-2" />
                Create Will with iDecide
              </Button>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold mb-4">Upload Documents</h2>
            <div className="space-y-4">
              <p className="text-gray-600">
                Upload your existing will or trust documents for safekeeping.
              </p>
              <div className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-center"
                  onClick={() => {
                    setUploadType('will');
                    setShowUploadModal(true);
                  }}
                >
                  <Upload size={20} className="mr-2" />
                  Upload Existing Will
                </Button>
                <Button 
                  variant="outline" 
                  className="w-full justify-center"
                  onClick={() => {
                    setUploadType('trust');
                    setShowUploadModal(true);
                  }}
                >
                  <Upload size={20} className="mr-2" />
                  Upload Existing Trust
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <Card>
        <div className="p-6">
          <h2 className="text-lg font-semibold mb-4">Your Documents</h2>
          <div className="space-y-4">
            {documents.map(doc => (
              <motion.div
                key={doc.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center justify-between p-6 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-all duration-200"
              >
                <div className="flex items-center gap-4">
                  <div className="flex items-center justify-center w-12 h-12 bg-gray-100 rounded-lg">
                    {getFileIcon(doc.file_type || '')}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-lg">{doc.title}</h3>
                    <div className="flex items-center gap-6 text-sm text-gray-600 mt-1">
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                        {doc.type.toUpperCase()}
                      </span>
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                        {doc.status}
                      </span>
                      {doc.file_size && <span className="flex items-center gap-1"><FileText size={14} /> {formatFileSize(doc.file_size)}</span>}
                      {doc.last_reviewed && (
                        <span className="flex items-center gap-1"><Calendar size={14} /> {new Date(doc.last_reviewed).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    size="md" 
                    onClick={() => handleView(doc)} 
                    disabled={deleting === doc.id}
                    className="flex items-center gap-2 min-w-[100px]"
                  >
                    <Eye size={18} />
                    {doc.file_type === 'application/pdf' ? 'View PDF' : 'View'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="md" 
                    onClick={() => handleDownload(doc)} 
                    disabled={deleting === doc.id || downloading === doc.id}
                    className="flex items-center gap-2 min-w-[120px]"
                  >
                    {downloading === doc.id ? (
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                    ) : (
                      <Download size={18} />
                    )}
                    {downloading === doc.id ? 'Downloading...' : 'Download'}
                  </Button>
                  <Button 
                    variant="outline" 
                    size="md" 
                    onClick={() => handleShare(doc)} 
                    disabled={deleting === doc.id}
                    className="flex items-center gap-2 min-w-[100px]"
                  >
                    <Share2 size={18} />
                    Share
                  </Button>
                  <Button 
                    variant="outline" 
                    size="md" 
                    onClick={() => handleDelete(doc)} 
                    disabled={deleting === doc.id}
                    className="flex items-center gap-2 min-w-[100px] border-red-200 text-red-600 hover:bg-red-50"
                  >
                    {deleting === doc.id ? (
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-red-600 rounded-full animate-spin" />
                    ) : (
                      <Trash2 size={18} />
                    )}
                    {deleting === doc.id ? 'Deleting...' : 'Delete'}
                  </Button>
                </div>
              </motion.div>
            ))}

            {documents.length === 0 && !loading && (
              <div className="text-center py-12 text-gray-500">
                <div className="flex items-center justify-center w-20 h-20 bg-gray-100 rounded-full mx-auto mb-4">
                  <FileText size={48} className="text-gray-300" />
                </div>
                <p className="text-xl font-medium mb-2">No documents yet</p>
                <p className="text-sm mb-6">Upload your first will or trust document to get started.</p>
                <Button 
                  variant="primary" 
                  onClick={() => setShowUploadModal(true)}
                  className="flex items-center gap-2"
                >
                  <Upload size={20} />
                  Upload First Document
                </Button>
              </div>
            )}

            {loading && (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                <p className="text-gray-500 mt-4 text-lg">Loading your documents...</p>
              </div>
            )}
          </div>
        </div>
      </Card>

      <Modal
        isOpen={showWillBuilder}
        onClose={() => setShowWillBuilder(false)}
        title="Create Your Will"
        maxWidth="2xl"
      >
        <div className="space-y-8">
          <div className="relative pt-1">
            <div className="flex mb-2 items-center justify-between">
              <div>
                <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-blue-600 bg-blue-200">
                  Step {currentStep + 1} of {steps.length}
                </span>
              </div>
              <div className="text-right">
                <span className="text-xs font-semibold inline-block text-blue-600">
                  {Math.round(((currentStep + 1) / steps.length) * 100)}%
                </span>
              </div>
            </div>
            <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-blue-200">
              <div
                style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-blue-500 transition-all duration-500"
              />
            </div>
          </div>

          <h2 className="text-xl font-semibold text-gray-900">
            {steps[currentStep].title}
          </h2>

          <div className="mt-4">
            {steps[currentStep].component}
          </div>

          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={currentStep === 0}
            >
              Back
            </Button>
            <div className="space-x-2">
              <Button
                variant="outline"
                onClick={() => setShowWillBuilder(false)}
              >
                Cancel
              </Button>
              {currentStep === steps.length - 1 ? (
                <Button
                  variant="primary"
                  onClick={handleSave}
                >
                  Save Will
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={handleNext}
                >
                  Continue
                </Button>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <TrustBuilder 
        isOpen={showTrustBuilder} 
        onClose={() => setShowTrustBuilder(false)} 
      />

      {/* Upload Document Modal */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        title={`Upload ${uploadType === 'will' ? 'Will' : 'Trust'} Document`}
      >
        <div className="space-y-6">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
            <input
              type="file"
              onChange={handleUpload}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              className="hidden"
              id="file-upload"
              disabled={uploading}
            />
            <label
              htmlFor="file-upload"
              className={`cursor-pointer flex flex-col items-center justify-center ${uploading ? 'opacity-50' : ''}`}
            >
              {uploading ? (
                <div className="w-12 h-12 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin mb-4" />
              ) : (
                <Upload size={40} className="text-gray-400 mb-4" />
              )}
              <p className="text-sm text-gray-600">
                {uploading ? 'Uploading...' : 'Click to upload or drag and drop'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                PDF, DOC, DOCX, or images (max. 50MB)
              </p>
            </label>
          </div>

          {error && (
            <div className="p-4 bg-red-50 text-red-700 rounded-lg flex items-center gap-2">
              <AlertCircle size={20} />
              <p>{error}</p>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setShowUploadModal(false)}
              disabled={uploading}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* PDF Viewer Modal */}
      <Modal 
        isOpen={!!pdfViewerUrl} 
        onClose={() => setPdfViewerUrl(null)} 
        title="Document Viewer"
        maxWidth="xl"
        className="max-w-xl mx-auto"
      >
        <div className="flex flex-col" style={{height: '70vh'}}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-10 h-10 bg-gray-100 rounded-lg">
                <FileText size={24} className="text-red-500" />
              </div>
              <div>
                <h3 className="font-semibold text-lg">PDF Document</h3>
                <p className="text-sm text-gray-500">Viewing your uploaded document</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(pdfViewerUrl!, '_blank')}
                className="flex items-center gap-2"
              >
                <ExternalLink size={16} />
                Open in New Tab
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPdfViewerUrl(null)}
              >
                Close
              </Button>
            </div>
          </div>
          <div className="flex-1 bg-gray-100 rounded-lg overflow-auto">
            {pdfViewerUrl && (
              <iframe
                src={`${pdfViewerUrl}#toolbar=1&navpanes=1&scrollbar=1&view=FitH`}
                className="w-full h-full border-0 rounded-lg"
                title="PDF Viewer"
                style={{minHeight: '400px'}}
              />
            )}
          </div>
        </div>
      </Modal>

      {/* Share Modal */}
      <Modal isOpen={showShareModal} onClose={() => setShowShareModal(false)} title="Share Document">
        <div className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Share2 size={20} className="text-blue-600" />
              <h3 className="font-semibold text-blue-900">Share Document</h3>
            </div>
            <p className="text-sm text-blue-700">
              Share this document with someone by providing their email address and access level.
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={shareForm.email}
                onChange={(e) => handleShareFormChange('email', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="recipient@example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Access Level
              </label>
              <select
                value={shareForm.accessLevel}
                onChange={(e) => handleShareFormChange('accessLevel', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="view">View Only</option>
                <option value="comment">View & Comment</option>
                <option value="edit">View & Edit</option>
                <option value="full">Full Access</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {shareForm.accessLevel === 'view' && 'Recipient can only view the document'}
                {shareForm.accessLevel === 'comment' && 'Recipient can view and add comments'}
                {shareForm.accessLevel === 'edit' && 'Recipient can view and edit the document'}
                {shareForm.accessLevel === 'full' && 'Recipient has full access including sharing'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Note to Recipient (Optional)
              </label>
              <textarea
                value={shareForm.note}
                onChange={(e) => handleShareFormChange('note', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Add a personal message or instructions for the recipient..."
                rows={3}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button 
              variant="outline" 
              onClick={() => setShowShareModal(false)}
              size="md"
            >
              Cancel
            </Button>
            <Button 
              variant="primary" 
              onClick={handleShareSubmit}
              size="md"
              disabled={!shareForm.email.trim()}
              className="flex items-center gap-2"
            >
              <Share2 size={18} />
              Share Document
            </Button>
          </div>
        </div>
      </Modal>

      {/* View Modal for form-based docs */}
      <Modal 
        isOpen={!!viewDoc} 
        onClose={() => setViewDoc(null)} 
        title={viewDoc?.title || 'View Document'}
        maxWidth="4xl"
      >
        <div className="space-y-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">Document Information</h4>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium text-gray-600">Type:</span> {viewDoc?.type}
              </div>
              <div>
                <span className="font-medium text-gray-600">Status:</span> {viewDoc?.status}
              </div>
              <div>
                <span className="font-medium text-gray-600">Created:</span> {viewDoc?.created_at ? new Date(viewDoc.created_at).toLocaleDateString() : 'N/A'}
              </div>
              <div>
                <span className="font-medium text-gray-600">Last Updated:</span> {viewDoc?.updated_at ? new Date(viewDoc.updated_at).toLocaleDateString() : 'N/A'}
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold text-gray-900 mb-2">Document Content</h4>
            <div className="bg-gray-100 p-4 rounded-lg max-h-96 overflow-auto">
              <pre className="text-xs text-gray-800 whitespace-pre-wrap">
                {viewDoc ? JSON.stringify(JSON.parse(viewDoc.content || '{}'), null, 2) : ''}
              </pre>
            </div>
          </div>
          
          <div className="flex justify-end">
            <Button 
              variant="outline" 
              onClick={() => setViewDoc(null)}
              size="md"
            >
              Close
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}